"use client";

import { Mail, Phone } from "lucide-react";
import { useTranslations } from "next-intl";
import { CONTACT } from "@/lib/constants/contact";
import { cn } from "@/lib/utils";

interface ContactQuickActionsProps {
  className?: string;
}

/**
 * Call and email, above the form, on narrow screens only.
 *
 * The form leads the page at every breakpoint and that is right for the primary
 * task — but below `lg` the channels rail sits after it, which puts the phone
 * number a whole form's worth of scrolling away. Someone whose car is making a
 * noise wants to call, not type, and nothing in the header or footer carries a
 * number. Two rows of buttons is the cheapest way to stop asking them to scroll
 * past the thing they did not come to do.
 *
 * `lg:hidden`, because at that width the rail is already beside the form and a
 * second copy would be noise.
 *
 * Neither is a filled button. Yellow is rationed to the submit, the active
 * segment, focus rings, and inline links; a filled yellow "Call us" above the
 * form would read as a second primary action competing with the one the page is
 * built around. Call carries the accent as text and border, email stays neutral,
 * so the two read as shortcuts rather than as the main path.
 *
 * Displayed number and dialled number come from different fields on purpose —
 * see `lib/constants/contact.ts`. Never collapse them.
 */
export function ContactQuickActions({ className }: ContactQuickActionsProps) {
  const t = useTranslations("contactInfo");

  return (
    <div className={cn("grid grid-cols-2 gap-3 lg:hidden", className)}>
      <a
        href={CONTACT.phones.primary.telHref}
        data-phone={CONTACT.phones.primary.key}
        aria-label={`${t("call.title")}: ${CONTACT.phoneWithCountry}`}
        className="flex h-12 items-center justify-center gap-2 rounded-full border border-yellow-500/40 px-4 text-sm font-medium text-yellow-500 transition-colors hover:border-yellow-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-site motion-reduce:transition-none"
      >
        <Phone aria-hidden="true" className="h-4 w-4" />
        {t("call.title")}
      </a>
      <a
        href="mailto:info@miller40.com"
        className="flex h-12 items-center justify-center gap-2 rounded-full border border-white/15 px-4 text-sm font-medium text-site-foreground transition-colors hover:border-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-site motion-reduce:transition-none"
      >
        <Mail aria-hidden="true" className="h-4 w-4" />
        {t("email.title")}
      </a>
    </div>
  );
}
